import { params } from '../modules/getUTM.js';

const SESSION_KEY = 'crmQuizQuestionnaireSession';
const RESULT_DATA_KEY = 'crmQuizResultData';
const RESULT_URL_KEY = 'crmQuizResultUrl';
const STATUS_EVENT = 'crmQuizQuestionnaireStatusChange';
export const CONTEXT_EVENT = 'crmQuizQuestionnaireContextUpdated';
const AUTO_SUBMIT_DELAY_MS = 5 * 60 * 1000;
const DEFAULT_FORM_ID = '8ad708bd-f7b0-ed11-83ff-000d3aba34cc';
const DEFAULT_RECIPIENTS = 'cortana@smart-it.com,pr@smart-it.com,dashe@smart-it.com';
const DEFAULT_TOPIC_PREFIX = 'Request_Questionnaire_SMART CRM';
const FINAL_STATUSES = new Set(['contact_submitted', 'auto_submitted']);
const DEBUG_LOG_LIMIT = 40;
const debugQuestionnaire = (message, data = {}) => {
	const timestamp = new Date().toISOString();
	const entry = {
		timestamp,
		scope: 'questionnaire',
		message,
		data,
	};

	try {
		const nextEntries = Array.isArray(window.__CRMQuizDebugEntries) ? window.__CRMQuizDebugEntries.slice(-DEBUG_LOG_LIMIT + 1) : [];
		nextEntries.push(entry);
		window.__CRMQuizDebugEntries = nextEntries;
		window.__CRMQuizDebugLastEntry = entry;
		window.__CRMQuizDebugLog = debugQuestionnaire;
	} catch {
		// Ignore logging sink failures.
	}

	try {
		// eslint-disable-next-line no-console
		console.log(`[crm-quiz][${entry.scope}] ${message}`, data);
	} catch {
		// Ignore console failures.
	}
};
const getSubmitEndpoint = () => {
	const restBase = String(window.CRMQuizSettings?.restUrl || window.CRMQuizResultPage?.restUrl || `${window.location.origin}/wp-json/crm-quiz/v1`).replace(
		/\/$/,
		'',
	);

	return `${restBase}/submit`;
};

let activeTimeout = null;
let activeTimeoutSessionId = null;

const readJson = (key) => {
	try {
		const raw = window.localStorage.getItem(key);
		return raw ? JSON.parse(raw) : null;
	} catch {
		return null;
	}
};

const writeJson = (key, value) => {
	window.localStorage.setItem(key, JSON.stringify(value));
};

const now = () => Date.now();

const normalizeResultUrl = (value = '') => {
	const input = String(value || '').trim();
	if (!input) {
		return '';
	}

	try {
		const url = new URL(input, window.location.origin);
		url.hash = '';
		return `${url.origin}${url.pathname.replace(/\/+$/, '/')}`;
	} catch {
		return input.replace(/[#?].*$/, '').replace(/\/+$/, '/');
	}
};

const normalizeQuestionnaireResultUrl = (value = '') => {
	const input = String(value || '').trim();
	if (!input) {
		return '';
	}

	try {
		const url = new URL(input, window.location.origin);
		url.hash = '';
		url.pathname = url.pathname.replace(/\/+$/, '/') || '/';
		return `${url.origin}${url.pathname}${url.search}`;
	} catch {
		const withoutHash = input.replace(/#.*$/, '');
		const [pathname, search = ''] = withoutHash.split('?');
		return `${pathname.replace(/\/+$/, '/')}${search ? `?${search}` : ''}`;
	}
};

const generateSessionId = () => {
	if (window.crypto?.randomUUID) {
		return window.crypto.randomUUID();
	}

	return `crm-quiz-${Math.random().toString(36).slice(2)}-${Date.now()}`;
};

const getLocaleSlug = (locale = 'ua') => String(locale || 'ua').toLowerCase();

const getUpperLocaleSlug = (locale = 'ua') => getLocaleSlug(locale).toUpperCase();

const getLanguageCode = (locale = 'ua') => {
	const slug = getUpperLocaleSlug(locale);

	return slug === 'UA' ? 'UK' : slug;
};

const sanitizeQuestionnaireMessage = (message = '', surveyAnswers = '', description = '') => {
	const value = String(message || '').trim();
	const answers = String(surveyAnswers || '').trim();
	const desc = String(description || '').trim();

	if (!value) {
		return '';
	}

	if (answers && value === answers) {
		return '';
	}

	if (desc && value === desc) {
		return '';
	}

	return value;
};

const getTopicPrefix = (topic = DEFAULT_TOPIC_PREFIX) => {
	const cleaned = String(topic || DEFAULT_TOPIC_PREFIX).trim();
	const prefix = cleaned.replace(/\{[^}]+\}\s*$/, '').trim();

	return prefix || DEFAULT_TOPIC_PREFIX;
};

export const buildQuestionnaireTopic = (locale = 'ua', topicPrefix = DEFAULT_TOPIC_PREFIX) => {
	return `${getTopicPrefix(topicPrefix)}_${getUpperLocaleSlug(locale)}`;
};

export const buildQuestionnaireAnswerText = (summary = []) => {
	if (!Array.isArray(summary) || summary.length === 0) {
		return '';
	}

	return summary
		.map((item, index) => {
			const question = String(item?.question || item?.questionText || item?.label || `Question ${index + 1}`).trim();
			const rawAnswer = item?.answer ?? item?.answerText ?? item?.selectedAnswer ?? item?.selected ?? item?.value ?? item?.label ?? item?.text ?? '';
			const answer = Array.isArray(rawAnswer)
				? rawAnswer
						.map((entry) => String(entry?.text || entry?.label || entry?.value || entry || '').trim())
						.filter(Boolean)
						.join(', ')
				: String(rawAnswer || '').trim();

			return `${question}: ${answer || '-'}`;
		})
		.join('\n');
};

export const buildQuestionnaireMessage = (context = {}, lead = {}) => {
	const userMessage = String(lead.message || lead['message'] || '').trim();
	const questionLines = String(buildQuestionnaireAnswerText(context.questionSummary || []) || context.survey_answers || context.description || '').trim();
	const recommendation = String(
		context.crm_result || context.result?.recommendationTitle || context.result?.recommendation || context.recommendationTitle || context.recommendation || '',
	).trim();
	const sections = [];

	if (userMessage) {
		sections.push(`User message:\n${userMessage}`);
	}

	if (questionLines) {
		sections.push(`Quiz answers:\n${questionLines}`);
	}

	if (recommendation) {
		sections.push(`Recommended system:\n${recommendation}`);
	}

	return sections.join('\n\n---\n\n');
};

const getLandingData = () => {
	const searchParams = new URLSearchParams(window.location.search);
	const landinglink = window.location.href;
	const landingsource = params.utm_source || params.source || document.referrer || '';

	return {
		landinglink,
		landingsource,
		landing_source: landingsource,
		utm_source: params.utm_source || searchParams.get('utm_source') || '',
		utm_medium: params.utm_medium || searchParams.get('utm_medium') || '',
		utm_campaign: params.utm_campaign || searchParams.get('utm_campaign') || '',
		utm_content: params.utm_content || searchParams.get('utm_content') || '',
		utm_term: params.utm_term || searchParams.get('utm_term') || '',
	};
};

const buildContextResult = (context = {}) => ({
	recommendationKey: context.result?.recommendationKey || context.result?.recommendation_key || context.variant || context.recommendationKey || 'smart',
	recommendationTitle: context.crm_result || context.result?.recommendationTitle || context.result?.recommendation || context.recommendationTitle || '',
	smartScore: Number(context.result?.smartScore ?? context.result?.smart_score ?? context.smartScore ?? 0),
	d365Score: Number(context.result?.d365Score ?? context.result?.d365_score ?? context.d365Score ?? 0),
	smartPct: Number(context.result?.smartPct ?? context.result?.smart_pct ?? context.smartPct ?? 0),
	d365Pct: Number(context.result?.d365Pct ?? context.result?.d365_pct ?? context.d365Pct ?? 0),
	confidence: context.result?.confidence || context.confidence || '',
});

export const normalizeQuestionnaireContext = (context = {}) => {
	const landingData = getLandingData();
	const result = buildContextResult(context);
	const locale = getLocaleSlug(context.locale || window.CRMQuizSettings?.locale || window.CRMQuizResultPage?.locale || document.documentElement.lang || 'ua');
	const questionSummary = Array.isArray(context.questionSummary) ? context.questionSummary : [];
	const surveyAnswers = buildQuestionnaireAnswerText(questionSummary) || context.survey_answers || context.description || '';

	return {
		...context,
		locale,
		result,
		questionSummary,
		survey_answers: surveyAnswers,
		description: context.description || surveyAnswers,
		message: sanitizeQuestionnaireMessage(context.message, surveyAnswers, context.description),
		resultUrl: context.resultUrl || context.result_url || window.location.href,
		result_url: context.result_url || context.resultUrl || window.location.href,
		resultId: context.resultId || context.result_id || '',
		result_id: context.result_id || context.resultId || '',
		resultSlug: context.resultSlug || context.result_slug || '',
		result_slug: context.result_slug || context.resultSlug || '',
		formId: context.formId || context.crm_id || DEFAULT_FORM_ID,
		crm_id: context.crm_id || context.formId || DEFAULT_FORM_ID,
		campaign_id: context.campaign_id || context.formId || context.crm_id || DEFAULT_FORM_ID,
		topicPrefix: context.topicPrefix || context.topic || DEFAULT_TOPIC_PREFIX,
		landinglink: context.landinglink || landingData.landinglink,
		landingsource: context.landingsource || landingData.landingsource,
		landing_source: context.landing_source || landingData.landing_source,
		utm_source: context.utm_source || landingData.utm_source,
		utm_medium: context.utm_medium || landingData.utm_medium,
		utm_campaign: context.utm_campaign || landingData.utm_campaign,
		utm_content: context.utm_content || landingData.utm_content,
		utm_term: context.utm_term || landingData.utm_term,
		userAgent: context.userAgent || navigator.userAgent || '',
		languageCode: context.languageCode || getLanguageCode(locale),
		ip: context.ip || '',
	};
};

const sameQuestionnaireResult = (left = {}, right = {}) => {
	const leftResultId = String(left.resultId || left.result_id || '').trim();
	const rightResultId = String(right.resultId || right.result_id || '').trim();
	const leftResultSlug = String(left.resultSlug || left.result_slug || '').trim();
	const rightResultSlug = String(right.resultSlug || right.result_slug || '').trim();
	const leftResultUrl = normalizeQuestionnaireResultUrl(left.resultUrl || left.result_url || '');
	const rightResultUrl = normalizeQuestionnaireResultUrl(right.resultUrl || right.result_url || '');

	if (leftResultId && rightResultId && leftResultId === rightResultId) {
		return true;
	}

	if (leftResultSlug && rightResultSlug && leftResultSlug === rightResultSlug) {
		return true;
	}

	return Boolean(leftResultUrl && rightResultUrl && leftResultUrl === rightResultUrl);
};

const isQuestionnaireSessionExpired = (session = {}) => Number(session.deadline || 0) <= now();

export const getQuestionnaireSession = () => readJson(SESSION_KEY);

export const setQuestionnaireSession = (session) => {
	writeJson(SESSION_KEY, session);
	window.__crmQuizQuestionnaireSession = session;
	window.__crmQuizQuestionnaireStatus = session?.status || '';
	return session;
};

export const clearQuestionnaireSessionTimer = () => {
	if (activeTimeout) {
		window.clearTimeout(activeTimeout);
		activeTimeout = null;
		activeTimeoutSessionId = null;
	}
};

const scheduleQuestionnaireAutoSubmit = (session) => {
	clearQuestionnaireSessionTimer();

	if (!session?.sessionId || session.status !== 'pending') {
		return;
	}

	const delay = Math.max(0, Number(session.deadline || 0) - now());
	activeTimeoutSessionId = session.sessionId;
	debugQuestionnaire('auto submit scheduled', {
		sessionId: session.sessionId,
		delay,
		deadline: session.deadline || 0,
	});

	activeTimeout = window.setTimeout(async () => {
		try {
			const currentSession = getQuestionnaireSession();
			if (!currentSession || currentSession.sessionId !== session.sessionId || currentSession.status !== 'pending') {
				debugQuestionnaire('auto submit skipped', {
					sessionId: session.sessionId,
					currentStatus: currentSession?.status || 'missing',
				});
				return;
			}

			debugQuestionnaire('auto submit fired', {
				sessionId: session.sessionId,
			});

			await submitQuestionnaireLead(currentSession.context || session.context || {}, {}, 'auto');
		} catch (error) {
			debugQuestionnaire('auto submit error', {
				sessionId: session.sessionId,
				message: error instanceof Error ? error.message : String(error),
			});
		}
	}, delay);
};

export const dispatchQuestionnaireStatus = (session) => {
	window.__crmQuizQuestionnaireSession = session;
	window.__crmQuizQuestionnaireStatus = session?.status || '';
	window.dispatchEvent(
		new CustomEvent(STATUS_EVENT, {
			detail: {
				session,
				status: session?.status || 'pending',
			},
		}),
	);
};

export const dispatchQuestionnaireContextUpdate = () => {
	window.dispatchEvent(new Event(CONTEXT_EVENT));
};

export const ensureQuestionnaireSession = (context = {}) => {
	const normalizedContext = normalizeQuestionnaireContext(context);
	const existingSession = getQuestionnaireSession();
	debugQuestionnaire('ensureQuestionnaireSession', {
		existingStatus: existingSession?.status || 'none',
		existingSessionId: existingSession?.sessionId || '',
		contextKeys: Object.keys(normalizedContext || {}),
		resultUrl: normalizedContext.resultUrl || normalizedContext.result_url || '',
		resultId: normalizedContext.resultId || normalizedContext.result_id || '',
	});

	if (
		existingSession &&
		(existingSession.status === 'contact_submitted' || existingSession.status === 'auto_submitted') &&
		sameQuestionnaireResult(existingSession.context || {}, normalizedContext)
	) {
		clearQuestionnaireSessionTimer();
		return existingSession;
	}

	if (
		existingSession &&
		existingSession.status === 'pending' &&
		sameQuestionnaireResult(existingSession.context || {}, normalizedContext) &&
		!isQuestionnaireSessionExpired(existingSession)
	) {
		const mergedSession = {
			...existingSession,
			context: {
				...existingSession.context,
				...normalizedContext,
			},
		};

		setQuestionnaireSession(mergedSession);
		dispatchQuestionnaireStatus(mergedSession);
		scheduleQuestionnaireAutoSubmit(mergedSession);

		return mergedSession;
	}

	const session = {
		sessionId: existingSession?.sessionId || generateSessionId(),
		createdAt: existingSession?.createdAt || now(),
		deadline: existingSession?.deadline || now() + AUTO_SUBMIT_DELAY_MS,
		status: existingSession?.status === 'pending' ? 'pending' : 'pending',
		context: normalizedContext,
	};

	setQuestionnaireSession(session);
	dispatchQuestionnaireStatus(session);
	scheduleQuestionnaireAutoSubmit(session);
	debugQuestionnaire('session created', {
		sessionId: session.sessionId,
		status: session.status,
		deadline: session.deadline,
	});

	return session;
};

export const markQuestionnaireSession = (status, extra = {}) => {
	const currentSession = getQuestionnaireSession();
	if (!currentSession) {
		return null;
	}

	const nextSession = {
		...currentSession,
		...extra,
		status,
		updatedAt: now(),
	};

	setQuestionnaireSession(nextSession);
	dispatchQuestionnaireStatus(nextSession);
	debugQuestionnaire('session updated', {
		sessionId: nextSession.sessionId,
		status: nextSession.status,
		extraKeys: Object.keys(extra || {}),
	});

	if (status === 'pending') {
		scheduleQuestionnaireAutoSubmit(nextSession);
	} else {
		clearQuestionnaireSessionTimer();
	}

	return nextSession;
};

const buildQuestionnaireSubmitPayload = (context = {}, lead = {}, mode = 'contact') => {
	const normalizedContext = normalizeQuestionnaireContext(context);
	const combinedMessage = buildQuestionnaireMessage(normalizedContext, lead) || '';
	const landingData = getLandingData();
	const acceptanceValue = lead.acceptance || lead['accept-this'] || lead['accept-this[]'] || '';

	if (mode === 'auto') {
		return {
			data: {
				campaign_id: normalizedContext.campaign_id,
				topic: buildQuestionnaireTopic(normalizedContext.locale, normalizedContext.topicPrefix),
				questionnaire_mode: 'auto',
				languageCode: normalizedContext.languageCode,
				locale: normalizedContext.locale,
				result_id: normalizedContext.resultId || normalizedContext.result_id || '',
				result_url: normalizedContext.resultUrl || normalizedContext.result_url || '',
				result_slug: normalizedContext.resultSlug || normalizedContext.result_slug || '',
				answers: normalizedContext.answers || [],
				result: normalizedContext.result || {},
				crm_result: normalizedContext.crm_result || '',
				questionSummary: normalizedContext.questionSummary || [],
				survey_answers: normalizedContext.survey_answers || normalizedContext.description || '',
				description: normalizedContext.description || normalizedContext.survey_answers || '',
				message: combinedMessage,
				acceptance: true,
				landinglink: normalizedContext.landinglink || landingData.landinglink,
				landingsource: normalizedContext.landingsource,
				landing_source: normalizedContext.landing_source,
				utm_source: normalizedContext.utm_source,
				utm_medium: normalizedContext.utm_medium,
				utm_campaign: normalizedContext.utm_campaign,
				utm_content: normalizedContext.utm_content,
				utm_term: normalizedContext.utm_term,
				userAgent: normalizedContext.userAgent,
				ip: normalizedContext.ip,
			},
		};
	}

	return {
		data: {
			campaign_id: normalizedContext.campaign_id,
			recipients: DEFAULT_RECIPIENTS,
			topic: buildQuestionnaireTopic(normalizedContext.locale, normalizedContext.topicPrefix),
			crmType: 'lead',
			questionnaire_mode: mode,
			languageCode: normalizedContext.languageCode,
			locale: normalizedContext.locale,
			result_id: normalizedContext.resultId || normalizedContext.result_id || '',
			result_url: normalizedContext.resultUrl || normalizedContext.result_url || '',
			result_slug: normalizedContext.resultSlug || normalizedContext.result_slug || '',
			questionSummary: normalizedContext.questionSummary || [],
			survey_answers: normalizedContext.survey_answers || normalizedContext.description || '',
			description: normalizedContext.description || normalizedContext.survey_answers || '',
			yourName: lead.yourName || lead.name || lead['your-name'] || '',
			yourSurName: lead.yourSurName || lead.lastname || lead['your-lastname'] || '',
			company: lead.company || lead['your-company'] || '',
			position: lead.position || lead['your-position'] || '',
			email: lead.email || lead['your-email'] || '',
			phone: lead.phone || lead['your-phone'] || '',
			message: combinedMessage,
			acceptance: mode === 'auto' ? true : Boolean(acceptanceValue),
			landinglink: lead.landinglink || normalizedContext.landinglink || landingData.landinglink,
			landingsource: lead.landingsource || normalizedContext.landingsource,
			landing_source: lead.landing_source || normalizedContext.landing_source,
			utm_source: lead.utm_source || normalizedContext.utm_source,
			utm_medium: lead.utm_medium || normalizedContext.utm_medium,
			utm_campaign: lead.utm_campaign || normalizedContext.utm_campaign,
			utm_content: lead.utm_content || normalizedContext.utm_content,
			utm_term: lead.utm_term || normalizedContext.utm_term,
			userAgent: lead.userAgent || normalizedContext.userAgent,
			ip: lead.ip || normalizedContext.ip,
		},
	};
};

const sendQuestionnairePayload = async (context = {}, lead = {}, mode = 'contact') => {
	const payloadContext = normalizeQuestionnaireContext(context);
	const endpoint = getSubmitEndpoint();
	debugQuestionnaire('send payload', {
		endpoint,
		mode,
		contextKeys: Object.keys(payloadContext || {}),
		leadKeys: Object.keys(lead || {}),
	});

	const response = await fetch(endpoint, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(buildQuestionnaireSubmitPayload(payloadContext, lead, mode)),
	});

	const responseBody = await response.text();
	let parsedBody = null;

	try {
		parsedBody = responseBody ? JSON.parse(responseBody) : null;
	} catch {
		parsedBody = null;
	}

	const responseSuccessful = response.ok && (parsedBody == null || (parsedBody.crm_sent !== false && parsedBody.success !== false));

	if (!responseSuccessful) {
		debugQuestionnaire('submit failed response', {
			status: response.status,
			statusText: response.statusText,
			endpoint,
			body: responseBody,
		});
		throw new Error('Failed to submit questionnaire');
	}

	debugQuestionnaire('submit response ok', {
		status: response.status,
		endpoint,
		body: responseBody,
	});
	return parsedBody || (responseBody ? JSON.parse(responseBody) : null);
};

export const startQuestionnaireAutoSubmit = (context = {}) => {
	const session = ensureQuestionnaireSession(context);

	return () => {
		if (getQuestionnaireSession()?.sessionId === session?.sessionId) {
			clearQuestionnaireSessionTimer();
		}
	};
};

export const submitQuestionnaireLead = async (context = {}, lead = {}, modeOverride = '') => {
	const session = getQuestionnaireSession();
	const sessionContext = session?.context || normalizeQuestionnaireContext(context);
	const resolvedMode =
		modeOverride ||
		(Object.keys(lead || {}).some((key) =>
			['name', 'lastname', 'company', 'position', 'email', 'phone', 'your-name', 'your-email', 'your-phone', 'your-company', 'your-position'].includes(key),
		)
			? 'contact'
			: 'auto');

	const inFlightStatus = resolvedMode === 'auto' ? 'auto_submitting' : 'contact_submitting';
	const finalStatus = resolvedMode === 'auto' ? 'auto_submitted' : 'contact_submitted';

	markQuestionnaireSession(inFlightStatus, {
		submittedAt: now(),
		questionnaireMode: resolvedMode,
	});
	debugQuestionnaire('submit started', {
		sessionId: session?.sessionId || '',
		mode: resolvedMode,
		inFlightStatus,
		finalStatus,
		hasLeadData: Object.keys(lead || {}).length > 0,
	});

	let payload;

	try {
		payload = await sendQuestionnairePayload(sessionContext, lead, resolvedMode);
	} catch (error) {
		debugQuestionnaire('submit error', {
			sessionId: session?.sessionId || '',
			mode: resolvedMode,
			message: error instanceof Error ? error.message : String(error),
		});
		markQuestionnaireSession(resolvedMode === 'auto' ? 'auto_failed' : 'contact_failed', {
			failedAt: now(),
			questionnaireMode: resolvedMode,
			error: error instanceof Error ? error.message : 'Failed to submit questionnaire',
		});

		throw error;
	}

	const nextSession = markQuestionnaireSession(finalStatus, {
		completedAt: now(),
		questionnaireMode: resolvedMode,
		response: payload,
	});
	debugQuestionnaire('submit completed', {
		sessionId: nextSession?.sessionId || '',
		mode: resolvedMode,
		finalStatus,
	});

	window.CRMQuizLeadContext = {
		...(window.CRMQuizLeadContext || {}),
		...sessionContext,
		lead: {
			...(lead || {}),
			message: buildQuestionnaireMessage(sessionContext, lead),
		},
		status: finalStatus,
		questionnaireMode: resolvedMode,
		response: payload,
	};
	dispatchQuestionnaireContextUpdate();

	return {
		...payload,
		session: nextSession,
	};
};

export const getQuestionnaireStatus = () => window.CRMQuizResultPage?.questionnaire_status || getQuestionnaireSession()?.status || 'idle';

export const subscribeQuestionnaireStatus = (handler) => {
	const listener = (event) => {
		handler(event.detail || {});
	};

	window.addEventListener(STATUS_EVENT, listener);

	return () => {
		window.removeEventListener(STATUS_EVENT, listener);
	};
};

export const getQuestionnaireStoredLead = () => ({});

export const getQuestionnaireStoredResultContext = () => readJson(SESSION_KEY)?.context || null;
